/**
 * GMA-1619 (CompassCare Web 4/14 — Alerts) — page composition.
 *
 * The emergency-response queue: alerts ordered by the pure alertsQueueModel
 * (severity → age, resolved sunk to the bottom), each row carrying a severity
 * badge, the client + location, a lifecycle state chip, and a relative age.
 * Selecting a row opens the shared DetailDrawer with the critical-fall
 * "Response Detail" body — the HARD fixed section headings
 * (RESPONSE_SECTION_HEADINGS) plus an action bar derived from the state
 * machine (nextStates → applyTransition), so an alert can only ever move
 * New → Acknowledged → Dispatched/Resolved and every transition is recorded in
 * its audit history.
 *
 * Presentational + fail-safe: `alerts` is injected by the route (testable) and
 * defaults to []; null/malformed rows can never break the render; section copy
 * that trips the forbidden-phrasing guard is replaced by a clinical fallback.
 * Colors/fonts/severity come from the single design-token set.
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import SeverityBadge from "../../components/web/SeverityBadge";
import DetailDrawer from "../../components/web/DetailDrawer";
import {
  ALERT_STATE_LABELS,
  RESPONSE_SECTION_HEADINGS,
  alertState,
  nextStates,
  applyTransition,
  sortAlertsForQueue,
  containsForbiddenPhrasing,
} from "./alertsQueueModel";
import { formatRelativeTime } from "../../utils/webFormat";
import { theme } from "../../styles/designTokens";

// Human action verbs for the state-machine targets (UI copy only — the states
// and their transitions live in the model).
const ACTION_LABELS = Object.freeze({
  acknowledged: "Acknowledge",
  dispatched: "Dispatch",
  resolved: "Resolve",
});

// Clinical fallback when a section has no copy (or its copy failed the
// forbidden-phrasing guard). "Silence is not reassurance" — say so explicitly.
const SECTION_FALLBACK = "No information recorded.";

function stateChip(alert) {
  const state = alertState(alert);
  const resolved = state === "resolved";
  return (
    <span
      data-testid="alert-state-chip"
      data-state={state}
      style={{
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        whiteSpace: "nowrap",
        border: `1px solid ${theme.border}`,
        background: resolved ? "transparent" : theme.surface,
        color: resolved ? theme.textMuted : theme.textPrimary,
      }}
    >
      {ALERT_STATE_LABELS[state]}
    </span>
  );
}

/**
 * Copy for one Response Detail section: the alert's own copy when it is a
 * non-empty string that passes the forbidden-phrasing guard, else the fallback.
 */
function sectionCopy(alert, heading) {
  const sections =
    alert !== null &&
    alert !== undefined &&
    typeof alert.responseSections === "object" &&
    alert.responseSections !== null
      ? alert.responseSections
      : {};
  const copy = sections[heading];
  if (
    typeof copy === "string" &&
    copy.trim() !== "" &&
    !containsForbiddenPhrasing(copy)
  ) {
    return copy;
  }
  return SECTION_FALLBACK;
}

export default function AlertsPage({
  alerts = [],
  now,
  actor = "Responder",
  onTransitionAlert,
}) {
  // Local working copy so drawer actions (Acknowledge/Dispatch/Resolve) update
  // the queue immediately; the route re-mounts with fresh data per visit.
  const [rows, setRows] = useState(() => (Array.isArray(alerts) ? alerts : []));
  const [selected, setSelected] = useState(null); // alert object | null

  // Re-sync when the parent hands down a new alerts array (e.g. a future live
  // feed) — without this, rows would keep showing the first render's snapshot.
  // Selection is cleared because it may reference an object no longer present
  // (transition() reference-matches rows, so a stale selection would no-op).
  useEffect(() => {
    setRows(Array.isArray(alerts) ? alerts : []);
    setSelected(null);
  }, [alerts]);

  const sorted = sortAlertsForQueue(rows, now).filter(Boolean);

  const transition = (to) => {
    if (selected === null || selected === undefined) return;
    const updated = applyTransition(selected, to, actor, Date.now());
    // Reference-match so rows without ids still update correctly.
    setRows((prev) => prev.map((row) => (row === selected ? updated : row)));
    setSelected(updated);
    if (typeof onTransitionAlert === "function") onTransitionAlert(updated, to);
  };

  return (
    <div
      data-testid="alerts-page"
      style={{
        display: "grid",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      <section
        data-testid="alerts-queue"
        aria-label="Alert response queue"
        style={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
        }}
      >
        {sorted.length === 0 ? (
          <p
            data-testid="alerts-queue-empty"
            style={{ padding: 16, margin: 0, color: theme.textMuted }}
          >
            No alerts in the queue
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {sorted.map((alert, index) => {
              const key =
                alert.id !== null && alert.id !== undefined
                  ? alert.id
                  : `${alert.severity}-${alert.createdAt}-${index}`;
              const label = alert.type || "Alert";
              // One fallback for BOTH the visible name and the aria-label, so
              // assistive tech hears exactly what is rendered.
              const clientName = alert.clientName || "Unknown client";
              const open = () => setSelected(alert);
              return (
                <li
                  key={key}
                  data-testid="alerts-queue-row"
                  data-severity={alert.severity || "unknown"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 16px",
                    borderBottom: `1px solid ${theme.border}`,
                  }}
                >
                  {/* The <li> stays a plain listitem for list semantics; the
                      interactive open-region carries the button role (keyboard
                      operable), mirroring AlertsRail. */}
                  <div
                    data-testid="alerts-queue-open"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open alert: ${label} for ${clientName}`}
                    onClick={open}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                        e.preventDefault();
                        open();
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flex: 1,
                      minWidth: 0,
                      cursor: "pointer",
                    }}
                  >
                    <SeverityBadge level={alert.severity} label={label} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span data-testid="alert-client" style={{ display: "block" }}>
                        {clientName}
                      </span>
                      {alert.location ? (
                        <span style={{ fontSize: 12, color: theme.textMuted }}>
                          {alert.location}
                        </span>
                      ) : null}
                    </span>
                    {stateChip(alert)}
                    <span
                      style={{
                        fontFamily: theme.fontMono,
                        fontSize: 12,
                        color: theme.textMuted,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatRelativeTime(alert.createdAt, now)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <DetailDrawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.clientName || "Alert detail" : ""}
        type="alert"
      >
        {selected ? (
          <div data-testid="response-detail" style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <SeverityBadge
                level={selected.severity}
                label={selected.type || "Alert"}
              />
              {stateChip(selected)}
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: theme.fontMono,
                  fontSize: 12,
                  color: theme.textMuted,
                }}
              >
                {formatRelativeTime(selected.createdAt, now)}
              </span>
            </div>
            {selected.location ? (
              <p style={{ margin: 0, fontSize: 13, color: theme.textMuted }}>
                {selected.location}
              </p>
            ) : null}

            {RESPONSE_SECTION_HEADINGS.map((heading) => (
              <section key={heading} aria-label={heading}>
                <h3
                  style={{
                    margin: "0 0 4px",
                    fontSize: 13,
                    color: theme.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {heading}
                </h3>
                <p style={{ margin: 0, fontSize: 14 }}>
                  {sectionCopy(selected, heading)}
                </p>
              </section>
            ))}

            <div
              role="group"
              aria-label="Response actions"
              data-testid="alert-actions"
              style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
            >
              {nextStates(selected).map((to) => (
                <button
                  key={to}
                  type="button"
                  data-testid={`alert-action-${to}`}
                  aria-label={`${ACTION_LABELS[to] || to} alert`}
                  onClick={() => transition(to)}
                  style={{
                    minHeight: 36,
                    padding: "6px 16px",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    background: theme.surface,
                    color: theme.textPrimary,
                    cursor: "pointer",
                    fontFamily: theme.fontUI,
                    fontSize: 13,
                  }}
                >
                  {ACTION_LABELS[to] || ALERT_STATE_LABELS[to] || to}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </DetailDrawer>
    </div>
  );
}

AlertsPage.propTypes = {
  /**
   * Response-queue alerts; { id, severity, type, clientName, location,
   * createdAt, state, history, responseSections }. Typed as a plain array (not
   * arrayOf(shape)) because the page is intentionally fail-safe for null /
   * malformed rows, and a strict element type would warn for those supported
   * inputs.
   */
  alerts: PropTypes.array,
  /** Reference time for relative-age display; defaults inside the model/format helpers. */
  now: PropTypes.number,
  /** Who is performing transitions — recorded in the audit history. */
  actor: PropTypes.string,
  /** Called as onTransitionAlert(updatedAlert, toState) after a transition applies. */
  onTransitionAlert: PropTypes.func,
};
